import { useNavigate } from 'react-router-dom';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownSection, DropdownItem, Button } from '@heroui/react';
import { usePlatformCapabilities } from '@/features/platform/hooks';
import { useTenant } from '@/contexts/TenantContext';
import { BuildingOffice2Icon, ChevronUpDownIcon, CheckIcon, PlusIcon } from '@/icons';

const ONBOARD_KEY = '__onboard_organization__';

/**
 * Organization switcher for the top bar. Non-interactive when the user belongs
 * to a single organization. Selecting a tenant re-scopes the session and resets cached data.
 */
export function TenantSwitcher() {
  const { current, memberships, isMulti, isSwitching, isLoading, switchTo } = useTenant();
  const { canOnboard } = usePlatformCapabilities();
  const navigate = useNavigate();
  if (!current) return null;
  if (!isMulti && !canOnboard) {
    // Single organization (or memberships still loading): static, non-interactive label.
    const hint = isLoading ? 'Loading organizations' : 'Only one organization available';
    return (
      <div
        className="hidden items-center gap-2 rounded-lg border border-border px-2.5 py-1.5 text-sm text-fg-muted md:flex"
        title={hint}
        aria-label={`${current.name}. ${hint}.`}
      >
        <BuildingOffice2Icon className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden="true" />
        <span className="max-w-[10rem] truncate">{current.name}</span>
      </div>
    );
  }

  const currentMembership = memberships.find((m) => m.id === current.id);

  return (
    <Dropdown
      placement="bottom-start"
      offset={6}
      // Portaled to <body> and layered above the sticky top bar (z-30), sidebar
      // (z-40) and every dashboard card; opaque surface so nothing shows through.
      classNames={{
        base: 'z-[60]',
        content: 'min-w-[18rem] max-w-[min(26rem,calc(100vw-2rem))] rounded-xl border border-border bg-surface-raised p-1 shadow-xl ring-1 ring-black/5',
      }}
    >
      <DropdownTrigger>
        <Button variant="bordered" size="sm" isLoading={isSwitching}
          aria-label={`Organization: ${current.name}. Switch organization`}
          startContent={<BuildingOffice2Icon className="h-4 w-4 shrink-0 text-fg-subtle" />}
          endContent={<ChevronUpDownIcon className="h-4 w-4 shrink-0 text-fg-subtle" />}
          className="max-w-[14rem] justify-start border-border bg-surface text-fg">
          <span className="truncate">{current.name}</span>
          {currentMembership?.accountCode && (
            <span className="hidden font-mono text-xs text-fg-subtle xl:inline">{currentMembership.accountCode}</span>
          )}
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Switch organization"
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={new Set([current.id])}
        hideSelectedIcon
        onAction={(key) => (key === ONBOARD_KEY ? navigate('/organizations/new') : void switchTo(String(key)))}
        itemClasses={{
          base: 'w-full gap-3 rounded-lg px-3 py-2.5 text-fg data-[hover=true]:!bg-fg/5 data-[hover=true]:!text-fg data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-brand',
          title: 'truncate text-sm font-medium',
          description: 'font-mono text-xs text-fg-subtle',
        }}
      >
        <DropdownSection title="Organizations" showDivider={canOnboard}
          classNames={{ heading: 'px-3 pb-1 pt-1.5 text-xs font-semibold uppercase tracking-wide text-fg-subtle', divider: 'my-1 bg-border' }}>
          {memberships.map((m) => {
            const selected = m.id === current.id;
            return (
              <DropdownItem key={m.id}
                textValue={m.name}
                data-testid={`org-option-${m.accountCode ?? m.id}`}
                className={selected
                  ? '!bg-brand/10 !text-brand ring-1 ring-inset ring-brand/30 data-[hover=true]:!bg-brand/15'
                  : undefined}
                startContent={<BuildingOffice2Icon className={`h-5 w-5 shrink-0 ${selected ? 'text-brand' : 'text-fg-subtle'}`} aria-hidden="true" />}
                endContent={selected ? <CheckIcon className="h-4 w-4 shrink-0 text-brand" aria-label="Current organization" /> : null}
                description={[m.accountCode, m.role].filter(Boolean).join(' · ')}>
                {m.name}
              </DropdownItem>
            );
          })}
        </DropdownSection>
        {canOnboard ? (
          <DropdownSection aria-label="Platform actions">
            <DropdownItem key={ONBOARD_KEY} textValue="Onboard organization" startContent={<PlusIcon className="h-4 w-4 shrink-0 text-fg-subtle" />}>
              Onboard organization…
            </DropdownItem>
          </DropdownSection>
        ) : null}
      </DropdownMenu>
    </Dropdown>
  );
}
